<template>
  <modal-wrap
    class="sender-editor-dialog"
    :name="modalName"
    :header-class="'sender-address-header'"
    body-class="sender-address-modal"
    :hide-footer="true"
    size="xl"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">{{ modalTitle }}</h4>
        <b-badge v-if="isDefaultSenderAddress" variant="success" class="ml-2"> Default </b-badge>
        <b-badge v-if="selectedSender.hasCustomPrices" variant="success" class="ml-2"> Sender prices </b-badge>
      </div>
    </template>
    <template #default>
      <VeeForm @submit="handleSubmit">
        <div class="sender-editor">
          <p class="sender-intro">
            Add the sender identity, address and contact details used for shipments. All senders stay on this account.
            Fields marked * are required.
          </p>
          <fieldset :disabled="saving">
            <legend>Sender address</legend>
            <div class="sender-fields">
              <div class="sender-field">
                <Field
                  v-slot="{ field, errors }"
                  :model-value="selectedSender.companyName"
                  name="companyName"
                  :rules="{ required: true, min: 3 }"
                >
                  <label for="sender-companyName">Company name *</label><input
                    id="sender-companyName"
                    v-bind="field"
                    v-model="selectedSender.companyName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                </Field>
              </div>
              <div class="sender-field">
                <Field
                  v-slot="{ field, errors }"
                  :model-value="selectedSender.countryCode"
                  name="countryCode"
                  :rules="{ required: true }"
                >
                  <label for="sender-countryCode">Country *</label><select
                    id="sender-countryCode"
                    v-bind="field"
                    v-model="selectedSender.countryCode"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  >
                    <option :value="''">Select country</option>
                    <option v-for="country in countries" :key="country.value" :value="country.value">
                      {{ country.text }}
                    </option>
                  </select><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                </Field>
              </div>
              <div class="sender-field">
                <Field
                  v-slot="{ field, errors }"
                  :model-value="selectedSender.addressLine"
                  name="addressLine"
                  :rules="{ required: true }"
                >
                  <label for="sender-addressLine">Street address *</label><input
                    id="sender-addressLine"
                    v-bind="field"
                    v-model="selectedSender.addressLine"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                </Field>
              </div>
              <div class="sender-field">
                <Field v-slot="{ field, errors }" :model-value="selectedSender.addressLine2" name="addressLine2">
                  <label for="sender-addressLine2">Address line 2 (optional)</label><input
                    id="sender-addressLine2"
                    v-bind="field"
                    v-model="selectedSender.addressLine2"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                </Field>
              </div>
              <div class="sender-field">
                <Field
                  v-slot="{ field, errors }"
                  :model-value="selectedSender.postalCode"
                  name="postalCode"
                  :rules="`required|zip:${selectedSender.countryCode}`"
                >
                  <label for="sender-postalCode">Postcode *</label><input
                    id="sender-postalCode"
                    v-bind="field"
                    v-model="selectedSender.postalCode"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                </Field>
              </div>
              <div class="sender-field">
                <Field
                  v-slot="{ field, errors }"
                  :model-value="selectedSender.city"
                  name="city"
                  :rules="{ required: true }"
                >
                  <label for="sender-city">City *</label><input
                    id="sender-city"
                    v-bind="field"
                    v-model="selectedSender.city"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </fieldset>
          <fieldset :disabled="saving">
            <legend>Contact details</legend>
            <div class="sender-fields">
              <div class="sender-field">
                <Field
                  v-slot="{ field, errors }"
                  :model-value="selectedSender.email"
                  name="email"
                  :rules="{ required: true, email: true }"
                >
                  <label for="sender-email">Email address *</label><input
                    id="sender-email"
                    v-bind="field"
                    v-model="selectedSender.email"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                </Field>
              </div>
              <div class="sender-field">
                <Field
                  v-slot="{ field, errors }"
                  :model-value="selectedSender.phoneNumber"
                  name="phoneNumber"
                  :rules="`required|phone:${selectedSender.countryCode}`"
                >
                  <label for="sender-phoneNumber">Phone number *</label><input
                    id="sender-phoneNumber"
                    v-bind="field"
                    v-model="selectedSender.phoneNumber"
                    type="tel"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </fieldset>
          <fieldset :disabled="saving">
            <legend>Sender profile</legend>
            <div class="sender-fields">
              <div v-if="selectedSender.id" class="sender-field">
                <label for="sender-profile-id">Sender ID for API bookings</label>
                <input
                  id="sender-profile-id"
                  :value="selectedSender.id"
                  class="form-control"
                  type="text"
                  readonly
                  aria-describedby="sender-profile-id-help"
                />
                <p id="sender-profile-id-help" class="mb-0">
                  Use this value as senderAddressId with your existing API key.
                </p>
              </div>
              <div v-if="isStaff" class="sender-field">
                <label for="sender-branding">Tracking and notification branding</label>
                <input
                  id="sender-branding"
                  v-model="selectedSender.branding"
                  class="form-control"
                  type="text"
                  maxlength="50"
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  aria-describedby="sender-branding-help"
                />
                <p id="sender-branding-help" class="mb-0">
                  Enter a configured brand, such as charma or virea, using lowercase letters, numbers or hyphens.
                  Account branding takes priority; this is used only when the account has no branding. Leave blank
                  to use account settings. Once used, create a new sender to change its branding.
                  Carrier notifications follow the selected delivery service.
                </p>
              </div>
              <p v-else class="mb-0">
                Sender branding: {{ selectedSender.branding || 'Account branding' }}.
                Account branding takes priority. Managed by PacSort.
              </p>
            </div>
          </fieldset>
          <details class="sender-options">
            <summary>Additional settings (optional)</summary>
            <p>
              Identifiers and an optional sender override for shipments to a specific country. Leave blank to use the
              general default.
            </p>
            <fieldset :disabled="saving">
              <legend class="visually-hidden">Additional settings</legend>
              <div class="sender-fields">
                <div class="sender-field">
                  <Field v-slot="{ field, errors }" :model-value="selectedSender.coAddress" name="coAddress">
                    <label for="sender-coAddress">C/O (optional)</label><input
                      id="sender-coAddress"
                      v-bind="field"
                      v-model="selectedSender.coAddress"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                    /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                  </Field>
                </div>
                <div class="sender-field">
                  <Field
                    v-slot="{ field, errors }"
                    :model-value="selectedSender.companyShortName"
                    name="companyShortName"
                  >
                    <label for="sender-companyShortName">Company short name (optional)</label><input
                      id="sender-companyShortName"
                      v-bind="field"
                      v-model="selectedSender.companyShortName"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                    /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                  </Field>
                </div>
                <div v-if="isStaff" class="sender-field">
                  <Field
                    v-slot="{ field, errors }"
                    :model-value="selectedSender.clientNumber"
                    name="clientNumber"
                    :rules="{ max: 16 }"
                  >
                    <label for="sender-clientNumber">Client number (optional)</label><input
                      id="sender-clientNumber"
                      v-bind="field"
                      v-model="selectedSender.clientNumber"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                    /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                  </Field>
                </div>
                <div class="sender-field">
                  <Field
                    v-slot="{ field, errors }"
                    :model-value="selectedSender.externalId"
                    name="externalId"
                    :rules="{ max: 64 }"
                  >
                    <label for="sender-externalId">External ID (optional)</label><input
                      id="sender-externalId"
                      v-bind="field"
                      v-model="selectedSender.externalId"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                    /><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                  </Field>
                </div>
                <div class="sender-field">
                  <Field v-slot="{ field, errors }" :model-value="selectedSender.defaultFor" name="defaultFor">
                    <label for="sender-defaultFor">Default for destination country (optional)</label><select
                      id="sender-defaultFor"
                      v-bind="field"
                      v-model="selectedSender.defaultFor"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                    >
                      <option :value="null">No country override</option>
                      <option v-for="country in countries" :key="country.value" :value="country.value">
                        {{ country.text }}
                      </option>
                    </select><span v-if="errors.length" class="text-danger" role="alert">{{ errors.join() }}</span>
                  </Field>
                </div>
              </div>
            </fieldset>
          </details>
          <!-- Tax Numbers Section -->
          <div v-if="address" class="mt-4 mb-3">
            <h6 class="mb-2">Tax Numbers (VOEC)</h6>
            <div v-if="!senderTaxNumbers.length">
              <p class="center-text">No tax numbers found for this sender address.</p>
            </div>
            <div v-else class="tax-list">
              <div v-for="(tn, idx) in senderTaxNumbers" :key="tn.id || idx" class="d-flex align-items-center mb-2">
                <div class="flex-grow-1 d-flex">
                  <div class="mr-2" style="width: 40%">
                    <CountryPicker
                      v-model="tn.countryCode"
                      :value="tn.countryCode"
                      :name="`sa_tax_country_${tn.id || idx}`"
                      placeholder="Country"
                    />
                  </div>
                  <div class="flex-grow-1">
                    <input
                      v-model="tn.taxNumber"
                      :name="`sa_tax_${tn.id || idx}`"
                      class="form-control"
                      placeholder="Tax number"
                      type="text"
                    />
                  </div>
                </div>
                <div class="ml-2 d-flex">
                  <b-button size="sm" variant="danger" @click="removeTaxNumber(tn, idx)"> Delete </b-button>
                </div>
              </div>
            </div>
            <div class="mt-2">
              <b-button size="md" class="btn-green" variant="outline-secondary" @click="addTaxNumberRow">
                Add Tax Number
              </b-button>
            </div>
          </div>

          <p v-if="saveError" role="alert" class="text-danger">
            {{ saveError }} Your entries are still here.
          </p>
          <div class="sender-editor-actions">
            <b-button
              v-if="address && !isDefaultSenderAddress"
              type="button"
              variant="outline-secondary"
              :disabled="saving"
              @click="setSenderAddressAsDefault"
            >
              Set as default
            </b-button>
            <b-button type="button" variant="outline-secondary" :disabled="saving" @click="closeAddressModal">
              Cancel
            </b-button>
            <b-button type="submit" class="btn-green" :disabled="saving">
              {{ saving ? 'Saving…' : address ? 'Save changes' : 'Add sender address' }}
            </b-button>
          </div>
        </div>
      </VeeForm>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { CustomerModalData } from '@/types/Components/Modals'
import type { AxiosErrors } from '@/types/Components/Response'
import type { Address } from '@/types/Models/Customer'
import type { TaxNumber } from '@/types/Models/TaxNumber'
import { notify } from '@kyvg/vue3-notification'
import { BBadge, BButton } from 'bootstrap-vue-next'
import { Form as VeeForm, Field } from 'vee-validate'
import { defineComponent, computed, ref, onMounted, watch, toRaw, type Ref, type ComputedRef } from 'vue'
import { SENDER_ADDRESS_ACTION } from '@/const/ModalTypes'
import { findCountryCode, findCountryName } from '@/services/Country'
import { none } from '@/services/Helpers'
import { getValidationState } from '@/services/Validation'
import { useAccountStore } from '@/store/account'
import { useCustomersSenderAddressesStore } from '@/store/customers/senderAddresses'
import { useSenderAddressTaxNumbersStore } from '@/store/customers/senderAddressTaxNumbers'
import { useVueNextModalStore } from '@/store/vueNextModal'
import countries from '@/assets/files/countries.json'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import CountryPicker from '@/views/Components/Elements/Form/CountryPicker.vue'

export default defineComponent({
  name: 'SenderAddressActionModal',
  components: {
    BButton,
    BBadge,
    ModalWrap,
    CountryPicker,
    VeeForm,
    Field,
  },
  emits: ['refresh'],
  setup(_, { emit }) {
    const accountStore = useAccountStore()
    const modalStore = useVueNextModalStore()
    const customersSenderAddressesStore = useCustomersSenderAddressesStore()
    const taxNumbersStore = useSenderAddressTaxNumbersStore()

    const modalName: string = SENDER_ADDRESS_ACTION
    const emptyAddressData: Address = {
      branding: null,
      id: '',
      phoneNumber: '',
      phoneType: null,
      email: '',
      emailIsFake: false,
      countryCode: '',
      city: '',
      postalCode: '',
      addressLine: '',
      addressLine2: '',
      companyName: '',
      companyShortName: '',
      coAddress: '',
      defaultFor: null,
      hasCustomPrices: false,
      type: 'sender',
      isDefault: false,
      clientNumber: '',
      externalId: '',
    }
    const modalData: ComputedRef<CustomerModalData> = computed(() => modalStore.modalPropsByName(modalName))
    const countryName: Ref<string> = ref('')
    const selectedSender: Ref<Address> = ref({ ...emptyAddressData })
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const address: ComputedRef<string> = computed(() => modalData.value?.id || '')
    const modalTitle: ComputedRef<string> = computed(() =>
      address.value ? 'Edit sender address' : 'Add sender address',
    )
    const isDefaultSenderAddress: ComputedRef<boolean> = computed(
      () => address.value !== '' && !!selectedSender.value.isDefault,
    )

    const senderTaxNumbers: ComputedRef<TaxNumber[]> = computed(() => taxNumbersStore.taxNumbersWithUi)

    const saving: Ref<boolean> = ref(false)
    const saveError: Ref<string> = ref('')
    const handleSubmit = (): void => {
      if (saving.value) return
      saving.value = true
      saveError.value = ''
      const save = address.value
        ? updateSenderAddress()
            .then(() => taxNumbersStore.saveAll(modalData.value.customerId, address.value))
            .then(closeAddressModal)
        : createSenderAddress()
      save
        .then(() => notify({ group: 'success', text: 'Sender address saved.' }))
        .catch((error: AxiosErrors) => {
          saveError.value = error.message || 'Could not save the sender address. Please try again.'
        })
        .finally(() => {
          saving.value = false
        })
    }

    const prepareRequest = (): Address => {
      const data: Address = { ...toRaw(selectedSender.value), branding: selectedSender.value.branding || null }
      if (!isStaff.value) {
        delete data.clientNumber
        delete data.branding
      }

      return data
    }

    const closeAddressModal = (): void => {
      if (modalData.value.customerId) {
        customersSenderAddressesStore.fetch(modalData.value.customerId)
      }
      taxNumbersStore.reset()
      modalStore.closeModal(modalName)
      emit('refresh')
    }

    const createSenderAddress = (): Promise<void> => {
      return customersSenderAddressesStore
        .store({ companyId: modalData.value.customerId, data: prepareRequest() })
        .then(closeAddressModal)
    }

    const updateSenderAddress = (): Promise<void> => {
      const payload = {
        companyId: modalData.value.customerId,
        addressId: address.value,
        sender: prepareRequest(),
      }
      return customersSenderAddressesStore.updateSenderAddress(payload).then(none)
    }

    const destroySenderAddress = (): void => {
      customersSenderAddressesStore
        .destroySenderAddress({ companyId: modalData.value.customerId, addressId: address.value })
        .then(closeAddressModal)
        .catch(none)
    }

    const setSenderAddressAsDefault = (): void => {
      customersSenderAddressesStore
        .setAsDefaultSenderAddress({ companyId: modalData.value.customerId, addressId: address.value })
        .then(loadAddresses)
        .catch(none)
    }

    const selectCountry = (country?: string | null): void => {
      if (!country || country.length === 0) {
        countryName.value = ''
        selectedSender.value.countryCode = ''
        return
      }
      const cleanCode = country.split(' - ')[0] || ''
      selectedSender.value.countryCode = findCountryCode(cleanCode) ?? ''
      countryName.value = findCountryName(cleanCode) ?? ''
    }

    const selectDefaultFor = (country?: string | null): void => {
      if (!country || country.length === 0) {
        countryName.value = ''
        selectedSender.value.defaultFor = ''
        return
      }
      const cleanCode = country.split(' - ')[0] || ''
      selectedSender.value.defaultFor = findCountryCode(cleanCode) ?? ''
      countryName.value = findCountryName(cleanCode) ?? ''
    }

    const loadAddresses = (): void => {
      if (address.value) {
        customersSenderAddressesStore
          .selectSenderAddress({ companyId: modalData.value.customerId, addressId: address.value })
          .then(() => {
            selectedSender.value = customersSenderAddressesStore.selectedSenderAddressValue || { ...emptyAddressData }
            selectCountry(selectedSender.value.countryCode)
          })
          .catch(none)
      } else {
        selectedSender.value = { ...emptyAddressData }
      }
    }

    watch(address, loadAddresses, { deep: true })

    watch(
      () => modalStore.isModalVisible(modalName),
      (visible) => {
        if (visible) {
          saveError.value = ''
          loadAddresses()
        }
      },
    )
    onMounted(loadAddresses)

    const addTaxNumberRow = (): void => {
      taxNumbersStore.addLocal()
    }

    const removeTaxNumber = (tn: { id?: string }, idx: number): void => {
      if (!modalData.value?.customerId || !address.value) {
        taxNumbersStore.removeLocal(tn.id)
        return
      }
      taxNumbersStore.deleteTaxNumber(modalData.value.customerId, address.value, tn.id)
    }

    watch(address, (val) => {
      if (val && modalData.value?.customerId) {
        taxNumbersStore.fetch(modalData.value.customerId, val)
      } else {
        taxNumbersStore.reset()
      }
    })

    return {
      countries,
      saving,
      saveError,
      closeAddressModal,
      address,
      countryName,
      modalName,
      modalData,
      selectedSender,
      isStaff,
      modalTitle,
      isDefaultSenderAddress,
      senderTaxNumbers,
      handleSubmit,
      createSenderAddress,
      updateSenderAddress,
      destroySenderAddress,
      setSenderAddressAsDefault,
      selectCountry,
      selectDefaultFor,
      getValidationState,
      addTaxNumberRow,
      removeTaxNumber,
    }
  },
})
</script>
